import type { IBatchData } from 'types/store';
import { BatchDataStatus } from 'configs/constants';
import loadingImage from 'utils/images/loadingImage';
import { submitWebhook, pollUntilCompleted } from 'services/webhook/webhookService';
import errorLogger, { BUGSNAG_STATUSES } from '@picsart/growth-rc/services/errorLogger';

import { persistAndReadItems } from './persistResults';
import { createEmptyPresetReplayResult } from './replayResult';
import type { IPresetReplayResult, IWebhookPreset } from './types';

const WEBHOOK_ACTION_LABEL = 'webhook';

async function resolveDimensions(url: string, metadata?: { width: number; height: number }): Promise<{ width: number; height: number }> {
  if (metadata?.width && metadata?.height) return metadata;
  try {
    const img = await loadingImage(url);
    return { width: img.naturalWidth, height: img.naturalHeight };
  } catch {
    return { width: 0, height: 0 };
  }
}

async function processItem(preset: IWebhookPreset, item: IBatchData, signal?: AbortSignal): Promise<IBatchData> {
  const { id } = await submitWebhook({
    webhookId: preset.flowId,
    params: { image_url: { url: item.result.src } },
  });
  const { url, metadata } = await pollUntilCompleted(id, signal);
  const { width, height } = await resolveDimensions(url, metadata);

  return {
    ...item,
    status: BatchDataStatus.DONE,
    pendingResult: { src: url, width, height },
  };
}

export async function replayWebhookPreset(preset: IWebhookPreset, selectedData: IBatchData[], signal?: AbortSignal): Promise<IPresetReplayResult> {
  const result = createEmptyPresetReplayResult();

  // The webhook backend can't fetch blob: URLs from the browser, so push items
  // through BatchDataService first — this uploads any unsynced blobs to CDN —
  // and read the persisted (CDN) URLs back before submitting.
  const itemsWithCdnUrls = await persistAndReadItems(selectedData);

  const settled = await Promise.allSettled(
    itemsWithCdnUrls.map(item => {
      if (item.status === BatchDataStatus.ERROR) return Promise.resolve(item);
      return processItem(preset, item, signal);
    }),
  );

  result.processedData = settled.map((entry, i) => {
    const original = itemsWithCdnUrls[i];
    if (entry.status === 'fulfilled') return entry.value;

    errorLogger.trigger(entry.reason as Error, BUGSNAG_STATUSES.ERROR);
    const reason = entry.reason instanceof Error ? entry.reason.message : 'unknown_error';
    result.errors.push({ action: WEBHOOK_ACTION_LABEL, success: false, skipReason: reason });
    return { ...original, status: BatchDataStatus.ERROR };
  });

  if (result.errors.length < selectedData.length) {
    result.completedSteps = 1;
  }

  return result;
}
